import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, Menu, MenuItem, Box } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : '#ffffff',
    borderRadius: 12,
    minWidth: 320,
    maxWidth: 320,
    padding: theme.spacing(1.25),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderTop: `4px solid ${props.color || theme.palette.primary.main}`,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
    height: 'calc(100vh - 205px)',
  }),
  columnTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    paddingRight: theme.spacing(0.5),
  },
  totalValue: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    fontWeight: 700,
  },
  countBadge: {
    minWidth: 28,
    height: 24,
    padding: theme.spacing(0, 1),
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.type === 'dark' ? theme.palette.action.hover : '#eef2f7',
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 700,
  },
  columnHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  titleWrap: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
  },
  titleLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.text.disabled,
    padding: theme.spacing(0.5),
    '&:hover': {
      color: theme.palette.text.secondary,
    },
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  actionsButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  emptyState: {
    minHeight: 120,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
    padding: theme.spacing(2),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin, onEditLane, onDeleteLane }) => {
  const classes = useStyles({ color });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const canManageLane = isAdmin && id !== 'lane0';

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            <Box className={classes.titleLine}>
              {isAdmin && (
                <div
                  {...provided.dragHandleProps}
                  className={classes.dragHandle}
                >
                  <DragIndicatorIcon fontSize="small" />
                </div>
              )}
              <Box className={classes.titleWrap}>
                <Box className={classes.titleLine}>
                  <Typography className={classes.columnTitle}>{title}</Typography>
                  <span className={classes.countBadge}>{tickets.length}</span>
                </Box>
                <Typography className={classes.totalValue}>
                  Total: R$ {totalValue.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            {canManageLane && (
              <>
                <IconButton
                  size="small"
                  className={classes.actionsButton}
                  onClick={event => setAnchorEl(event.currentTarget)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onEditLane();
                    }}
                  >
                    <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                    Editar
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onDeleteLane();
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                    Excluir
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tickets.length === 0 && (
                  <div className={classes.emptyState}>Nenhum ticket nesta etapa.</div>
                )}
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanColumn;
